import { Fragment, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { DensityNote } from '@/components/px/DensityNote'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { KpiCard } from '@/components/px/KpiCard'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { adminNav } from '@/components/px/nav'
import { selectActiveOrgId, selectActiveRoleKey } from '@/features/auth/slices/sessionSlice'
import { MemberChip } from '@/features/projects/components/MemberChip'
import { eurThin, healthTone, phaseDisplayName } from '@/features/projects/format'
import { gateTone, selectAdminKpis, selectAdminPortfolioRows } from '@/features/projects/adminPortfolioSelectors'
import {
  adminPortfolioOpened,
  adminPortfolioRetried,
  fetchAdminPortfolio,
  selectAdminPortfolioError,
  selectAdminPortfolioLoadedKey
} from '@/features/projects/slices/adminPortfolioSlice'
import type { AdminPortfolioRow } from '@/features/projects/types'
import { asApiError } from '@/services/apiError'
import { useAppDispatch, useAppSelector } from '@/store'

/** The seven columns, in table order; `NUMERIC` decides right-alignment (AC-13). */
const COLUMNS = ['engagement', 'office', 'deliveryPhase', 'gate', 'health', 'contract', 'people'] as const
const NUMERIC = new Set<(typeof COLUMNS)[number]>(['contract', 'people'])

/**
 * `/admin/projects` — the org-admin's overview: every real engagement, the
 * gate it is sitting on, and exactly who is linked to it (TASK-17A2C062,
 * PX-W7.9). One request (`GET /{orgId}/admin-portfolio`, the ONE deliberate
 * exception to the roster ACL — project memory `px-org-admin-portfolio-read-
 * all`): the org-admin route gate is the authorisation, so this page shows
 * EVERY enabled engagement whether or not the caller is staffed on it.
 *
 * Agon's rulings this ticket carries forward: no Lead column (there is no
 * lead field, and guessing one from free-text role would be data dressed up
 * as a fact), no allocation percentage on the staffing line (no staffing
 * backend), contract value shown verbatim (an admin surface is not
 * redacted).
 */
export default function AdminProjectsPage() {
  const dispatch = useAppDispatch()
  const { t } = useTranslation('portfolio')
  const role = useAppSelector(selectActiveRoleKey)
  const orgId = useAppSelector(selectActiveOrgId)
  const rows = useAppSelector(selectAdminPortfolioRows)
  const kpis = useAppSelector(selectAdminKpis)
  const error = useAppSelector(selectAdminPortfolioError)
  const loadedKey = useAppSelector(selectAdminPortfolioLoadedKey)

  useEffect(() => {
    dispatch(adminPortfolioOpened())
    if (orgId !== null) void dispatch(fetchAdminPortfolio(orgId))
  }, [dispatch, orgId])

  const onRetryClicked = (): void => {
    dispatch(adminPortfolioRetried())
    if (orgId !== null) void dispatch(fetchAdminPortfolio(orgId))
  }

  const loaded = orgId !== null && loadedKey === orgId
  // The pill states a count only once THIS org's read has landed clean: while
  // loading, on a 403/500, and after a failed reload (whose stale view the
  // slice keeps) it names the surface instead — "0 engagements" before the
  // read, or yesterday's figure over an error panel, would be a false fact
  // (TASK-17A2C062 fix cycle 1, F2/L1). AppShell's pill is a required string.
  const contextPill =
    loaded && error === null
      ? t('admin.contextPill', { count: kpis.engagements })
      : t('admin.contextPillNeutral')

  return (
    <AppShell
      moduleLabel={t('admin.chrome.moduleLabel')}
      breadcrumb={t('admin.chrome.breadcrumb')}
      contextPill={contextPill}
      nav={adminNav(role)}
    >
      <div className="w-full">
        <header className="max-w-3xl">
          <h1 className="font-serif text-2xl font-semibold text-foreground">{t('admin.title')}</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">{t('admin.lede')}</p>
        </header>

        {error?.status === 403 ? (
          <div className="mt-5">
            <ForbiddenPanel requested={`/${orgId ?? ''}/admin-portfolio`} />
          </div>
        ) : error !== null ? (
          <div className="mt-5">
            <ErrorPanel error={asApiError(error)} onRetryClicked={onRetryClicked} />
          </div>
        ) : !loaded ? (
          <div className="mt-5">
            <LoadingPanel label={t('admin.states.loading')} />
          </div>
        ) : rows.length === 0 ? (
          <div className="mt-5">
            <EmptyPanel title={t('admin.states.emptyTitle')} body={t('admin.states.emptyBody')} />
          </div>
        ) : (
          <>
            <section aria-label={t('admin.glanceAriaLabel')} className="mt-5 grid grid-cols-4 gap-4">
              <KpiCard
                label={t('admin.kpis.engagements')}
                figure={kpis.engagements}
                qualifier={t('admin.kpis.engagementsQualifier')}
              />
              <KpiCard
                label={t('admin.kpis.people')}
                figure={kpis.peopleLinked}
                qualifier={t('admin.kpis.peopleQualifier', { total: kpis.orgUserCount ?? 0 })}
              />
              <KpiCard
                label={t('admin.kpis.memberRows')}
                figure={kpis.memberRows}
                qualifier={t('admin.kpis.memberRowsQualifier')}
              />
              <KpiCard
                label={t('admin.kpis.gatesOpen')}
                figure={kpis.gatesOpen}
                qualifier={t('admin.kpis.gatesQualifier', {
                  clientSignOff: kpis.gatesClientSignOff,
                  internal: kpis.gatesInternal
                })}
              />
            </section>

            <div className="mt-4 rounded-lg border border-border bg-card px-4 py-2">
              <table className="w-full text-left">
                <caption className="sr-only">{t('admin.tableCaption')}</caption>
                <thead>
                  <tr>
                    {COLUMNS.map((key) => (
                      <th
                        key={key}
                        scope="col"
                        className={`pb-2 pr-3 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground ${
                          NUMERIC.has(key) ? 'text-right' : ''
                        }`}
                      >
                        {t(`admin.columns.${key}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row: AdminPortfolioRow) => (
                    <Fragment key={row.id}>
                      <tr data-testid="admin-project-row" data-project-id={row.id} className="border-t border-hairline">
                        <td className="py-2.5 pr-3 align-top">
                          <Link
                            to={`/project/${row.id}`}
                            className="font-medium text-foreground underline-offset-4 hover:underline"
                          >
                            {row.name}
                          </Link>
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {row.clientName ?? t('admin.none')}
                          </div>
                        </td>
                        <td className="py-2.5 pr-3 align-top">{row.office ?? t('admin.none')}</td>
                        <td className="py-2.5 pr-3 align-top">
                          <StatusPill tone="neutral">{phaseDisplayName(row.currentPhase)}</StatusPill>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {row.phaseAgeDays === undefined
                              ? t('admin.none')
                              : t('admin.phaseAge', { count: row.phaseAgeDays })}
                          </div>
                        </td>
                        <td className="py-2.5 pr-3 align-top">
                          {row.currentGate === undefined ? (
                            t('admin.none')
                          ) : (
                            <>
                              <div className="font-mono text-[0.75rem] font-semibold tracking-[0.06em] text-foreground">
                                {row.currentGate.title}
                              </div>
                              <div className="mt-1">
                                <StatusPill tone={gateTone(row.currentGate)}>
                                  {t(`admin.gate.status.${row.currentGate.status}`, {
                                    defaultValue: row.currentGate.status
                                  })}
                                </StatusPill>
                              </div>
                              {row.currentGate.open && (
                                <div className="mt-1 text-xs text-muted-foreground">
                                  {t('admin.gate.ageDays', { count: row.currentGate.ageDays })}
                                </div>
                              )}
                            </>
                          )}
                        </td>
                        <td className="py-2.5 pr-3 align-top">
                          {row.health === undefined ? (
                            t('admin.none')
                          ) : (
                            <StatusPill tone={healthTone(row.health)}>{row.health}</StatusPill>
                          )}
                        </td>
                        <td className="v-figure whitespace-nowrap py-2.5 pr-3 text-right align-top text-sm tabular-nums text-foreground">
                          {row.contractValue === undefined ? t('admin.none') : eurThin(row.contractValue)}
                        </td>
                        <td className="v-figure py-2.5 pr-1 text-right align-top text-sm font-semibold tabular-nums text-foreground">
                          {row.members.length}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={COLUMNS.length} className="pb-3 pr-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <SectionLabel className="mr-1">{t('admin.staffed')}</SectionLabel>
                            {row.members.length === 0 ? (
                              <span className="text-xs text-muted-foreground">{t('admin.noMembers')}</span>
                            ) : (
                              row.members.map((member, index) => (
                                <MemberChip
                                  key={member.userId ?? member.id ?? `${row.id}-${index}`}
                                  member={member}
                                />
                              ))
                            )}
                          </div>
                        </td>
                      </tr>
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <DensityNote>{t('admin.density')}</DensityNote>
      </div>
    </AppShell>
  )
}
